// hooks/useSearch.js
"use client";
import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useLanguage } from "../../contexts/LanguageContext";

export const useSearch = () => {
  const router = useRouter();
  const { t, currentLang, isHydrated, forceUpdateKey, getTranslation } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [lastSearchLang, setLastSearchLang] = useState(currentLang);

  // Language change listener - yaxshilangan
  useEffect(() => {
    const handleLanguageChange = () => {
      // Clear search when language changes
      if (lastSearchLang !== currentLang) {
        setSearchQuery("");
        setSearchResults([]);
        setShowResults(false);
        setSearchError(null);
        setLastSearchLang(currentLang);
      }
    };

    const handleForceUpdate = () => {
      // Force clear cache and refresh search if query exists
      if (searchQuery.trim().length > 1) {
        setTimeout(() => {
          searchForklifts(searchQuery);
        }, 100);
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('languageForceUpdate', handleForceUpdate);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('languageForceUpdate', handleForceUpdate);
    };
  }, [currentLang, lastSearchLang, searchQuery]);

  // Debounce search function - faqat hydration tugagandan keyin ishlaydi
  useEffect(() => {
    if (!isHydrated) return;
    
    const delayedSearch = setTimeout(() => {
      if (searchQuery.trim().length > 1) {
        searchForklifts(searchQuery);
      } else {
        setSearchResults([]);
        setShowResults(false);
        setSearchError(null);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchQuery, currentLang, isHydrated, forceUpdateKey]);

  // Search API function with translation support - yaxshilangan
  const searchForklifts = useCallback(async (query) => {
    if (!query || query.trim().length < 2) return;

    setIsLoading(true);
    setSearchError(null);
    
    try {
      // API dan qidiruv so'rovi yuborish - cache buster qo'shildi
      const response = await fetch(
        `https://api.jacforklift.uz/api/api/forklifts/?search=${encodeURIComponent(
          query
        )}&lang=${currentLang}&_t=${Date.now()}`, // Cache buster
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "Accept-Language": currentLang,
            "Cache-Control": "no-cache", // Force fresh data
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('seach natijasi' + data);
      

      // Handle different API response formats
      let results = [];
      if (data.results && Array.isArray(data.results)) {
        results = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        results = data.data;
      } else if (Array.isArray(data)) {
        results = data;
      }

      // Server ma'lumotlarini client translation bilan birlashtirish
      const processedResults = results.map(product => ({
        ...product,
        // Server-side name va description bo'lmasa, client-side fallback
        displayName: product.name || getLocalizedProductName(product, currentLang),
        displayDescription: product.description || getLocalizedDescription(product, currentLang),
        // Kategoriya nomini tarjima qilish
        categoryName: getCategoryTranslation(product.category?.name, currentLang),
        // Brand nomini tarjima qilish (agar kerak bo'lsa)
        brandName: getBrandTranslation(product.brand?.name, currentLang),
        // Status va boshqa ma'lumotlarni tarjima qilish
        conditionText: getConditionTranslation(product.condition),
        availabilityText: getAvailabilityTranslation(product.quantity_in_stock),
        // Narx ma'lumotini format qilish
        formattedPrice: formatPrice(product.price_usd, currentLang),
      }));

      setSearchResults(processedResults);
      setShowResults(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchError(getTranslation("search_error", "Qidirishda xatolik yuz berdi"));
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentLang, getTranslation]);

  // Helper functions for translations - yaxshilangan
  const getLocalizedProductName = (product, lang) => {
    // Agar server-side name bo'lmasa, fallback logic
    if (product.model) {
      return `${product.brand?.name || ''} ${product.model}`.trim();
    }
    return product.part_number || product.id || getTranslation("unnamed_product", "Nomsiz mahsulot");
  };

  const getLocalizedDescription = (product, lang) => {
    // Server-side description bo'lmasa, auto-generated description
    const features = [];
    if (product.capacity) features.push(`${product.capacity} ${getTranslation("ton", "tonna")}`);
    if (product.fuel_type) features.push(getFuelTypeTranslation(product.fuel_type));
    if (product.year) features.push(`${product.year} ${getTranslation("year", "yil")}`);
    
    return features.length > 0 
      ? features.join(', ')
      : getTranslation("no_description", "Tavsif mavjud emas");
  };

  const getCategoryTranslation = (categoryName, currentLang) => {
    if (!categoryName) return '';
    
    // Translation contextidan categoriya nomini topish
    const translatedCategory = getTranslation(categoryName, categoryName);
    return translatedCategory;
  };

  const getBrandTranslation = (brandName, currentLang) => {
    // Brendlar odatda tarjima qilinmaydi, lekin ba'zi hollarda kerak bo'lishi mumkin
    const brandTranslations = {
      'JAC': 'JAC',
      'Toyota': 'Toyota',
      'Hyundai': 'Hyundai',
      // Qo'shimcha brendlar...
    };
    
    return brandTranslations[brandName] || brandName;
  };

  const getConditionTranslation = (condition) => {
    if (!condition) return '';
    
    const conditionMap = {
      'new': getTranslation("new", "Yangi"),
      'used': getTranslation("used", "Ishlatilgan"),
      'excellent': getTranslation("excellent", "A'lo"),
      'good': getTranslation("good", "Yaxshi"),
      'fair': getTranslation("fair", "O'rtacha"),
    };
    
    return conditionMap[condition.toLowerCase()] || condition;
  };

  const getAvailabilityTranslation = (stock) => {
    if (stock === undefined || stock === null) return '';
    
    if (stock > 0) {
      return `${stock} ${getTranslation("available", "mavjud")}`;
    } else {
      return getTranslation("out_of_stock", "Omborda yo'q");
    }
  };

  const getFuelTypeTranslation = (fuelType) => {
    if (!fuelType) return '';
    
    const fuelMap = {
      'diesel': getTranslation("diesel", "Dizel"),
      'electric': getTranslation("electric", "Elektr"),
      'gas': getTranslation("gas", "Gaz"),
      'lpg': getTranslation("lpg", "LPG"),
      'petrol': getTranslation("petrol", "Benzin"),
      'gasoline': getTranslation("gasoline", "Benzin"),
    };
    
    return fuelMap[fuelType.toLowerCase()] || fuelType;
  };

  const formatPrice = (priceUsd, currentLang) => {
    if (!priceUsd || parseFloat(priceUsd) <= 0) {
      return getTranslation("price_on_request", "Narx so'raladi");
    }
    
    const price = parseFloat(priceUsd);
    
    // Tilga qarab narx formatlash
    switch (currentLang) {
      case 'uz':
        return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      case 'ru':
        return `$${price.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      case 'en':
        return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      default:
        return `$${price.toFixed(0)}`;
    }
  };

  // Handle search input change - yaxshilangan
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim().length === 0) {
      setShowResults(false);
      setSearchResults([]);
      setSearchError(null);
    }
  };

  // Handle product click with proper routing - yaxshilangan
  const handleProductClick = (productId, productType = 'forklift', event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Mahsulot turiga qarab to'g'ri route tanlash
    let route;
    switch (productType) {
      case 'spare_part':
        route = `/spareDetail/${productId}`;
        break;
      case 'forklift':
      default:
        route = `/karaDetail/${productId}`;
        break;
    }

    setTimeout(() => {
      setShowResults(false);
      setSearchQuery("");
      setSearchError(null);
      router.push(route);
    }, 100);
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (searchQuery.trim().length > 0) {
      setShowResults(true);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
    setSearchError(null);
  };

  // Image error handler with fallback
  const handleImageError = (e) => {
    e.target.style.display = "none";
    const placeholder = e.target.parentNode.querySelector(".image-placeholder");
    if (placeholder) {
      placeholder.style.display = "flex";
    } else {
      // Fallback image yaratish
      const fallbackDiv = document.createElement('div');
      fallbackDiv.className = 'w-full h-12 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs';
      fallbackDiv.textContent = getTranslation("no_image", "Rasm yo'q");
      e.target.parentNode.appendChild(fallbackDiv);
    }
  };

  // Search placeholder text with real-time translation - yaxshilangan
  const getSearchPlaceholder = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return getTranslation("searchPlaceholderMobile", "Qidiruv...");
    }
    return getTranslation("searchPlaceholder", "Forklift qidiruv...");
  };

  return {
    // State values
    searchQuery,
    searchResults,
    isLoading,
    showResults,
    searchError,
    currentLang,
    isHydrated,
    t,
    forceUpdateKey, // Add force update key
    
    // State setters
    setSearchQuery,
    setShowResults,
    
    // Event handlers
    handleSearchChange,
    handleProductClick,
    handleInputFocus,
    handleImageError,
    clearSearch,
    
    // Helper functions with real-time translation
    getSearchPlaceholder,
    formatPrice,
    getFuelTypeTranslation,
    getConditionTranslation,
    getAvailabilityTranslation,
    getCategoryTranslation,
    getTranslation, // Add direct access to getTranslation
    
    // API function
    searchForklifts,
  };
};